import React, { useContext } from 'react';
import {
  Button,
  Image,
  Link,
  Text,
  Title3,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useAcceptInvitationStyles } from '../styles';
import HeaderLogo from '../../../media/microsoft-advertising-logo-idp-header.png';

// "Learn more" target for the Microsoft personal-account explainer. Same Microsoft Advertising help
// host the pre-auth landing page uses for its sign-in help link.
const LEARN_MORE_URL = 'https://help.ads.microsoft.com/#apex/ads/en/60259/0';

export type ActivationPersonalAccountRequiredProps = {
  // The personal Microsoft (MSA) account email this invitation's user is registered with. Shown
  // inline so the user knows which account to sign in with. Optional — omitted when unavailable.
  email?: string | null;
  // "Continue with Microsoft" — sign the current AAD/Google session out and send the user to the
  // personal Microsoft (MSA) sign-in.
  onContinueWithMicrosoft: () => void;
  // "Go back" — sign the current AAD/Google session out and return to the default invitation choice.
  onGoBack: () => void;
};

// Shown when ActivationOptions reports the invited email is registered with a personal Microsoft
// (MSA) account, but the user signed in with a Microsoft work/school (AAD) or Google account. To
// accept the invitation they must re-authenticate with the personal account — both actions sign the
// current session out first (the URLs are resolved by the caller). Mirror of
// ActivationWorkAccountRequired.
export const ActivationPersonalAccountRequired: React.FC<ActivationPersonalAccountRequiredProps> = ({
  email,
  onContinueWithMicrosoft,
  onGoBack,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();

  const accountLine = email
    ? i18n.getString(
      _TL_('Your Microsoft Advertising account for {{email}} uses a personal Microsoft account.'),
      { email }
    )
    : i18n.getString(_TL_('Your Microsoft Advertising account uses a personal Microsoft account.'));

  return (
    <div className={classes.centeredPage}>
      <div
        className={classes.errorCard}
        role="region"
        aria-labelledby="activation-personal-required-title"
        aria-describedby="activation-personal-required-description"
      >
        <Image className={classes.brandLogo} src={HeaderLogo} alt={i18n.getString(_TL_('Microsoft Advertising'))} />

        <Title3 id="activation-personal-required-title" className={classes.errorTitle}>
          {i18n.getString(_TL_('Sign in with Microsoft Personal Account'))}
        </Title3>

        <div id="activation-personal-required-description" className={classes.errorBody}>
          <Text block>{accountLine}</Text>
          <Text block>
            {i18n.getString(_TL_('Tip: When Microsoft asks which account to use, choose Personal account.'))}
          </Text>
          <Text block>
            {i18n.getString(_TL_('To continue, sign in with your personal account.'))}
            {' '}
            <Link href={LEARN_MORE_URL} target="_blank" rel="noopener noreferrer">
              {i18n.getString(_TL_('Learn More'))}
            </Link>
          </Text>
        </div>

        <div className={classes.errorActions}>
          <Button
            className="personal-account-continue"
            appearance="primary"
            onClick={onContinueWithMicrosoft}
          >
            {i18n.getString(_TL_('Continue with Microsoft'))}
          </Button>
          <Button
            className="personal-account-go-back"
            appearance="secondary"
            onClick={onGoBack}
          >
            {i18n.getString(_TL_('Go back'))}
          </Button>
        </div>
      </div>
    </div>
  );
};
